import { oc } from "@orpc/contract";
import * as z from "zod";
import {
    DocsConfigSchema as DocsReadConfigSchema,
    DocsDefinitionSchema as DocsReadDefinitionSchema,
    LoadDocsForUrlResponseSchema
} from "../../../../client/docs-types/read.js";

export type {
    DocsConfig,
    DocsDefinition,
    LoadDocsForUrlResponse,
    NavigationConfig,
    NavigationItem
} from "../../../../client/docs-types/read.js";
// Re-export docs types that consumers depend on
export { DocsReadConfigSchema, DocsReadDefinitionSchema, LoadDocsForUrlResponseSchema };

export const GetDocsUrlMetadataInputSchema = z.object({
    url: z.string()
});

export const GetDocsUrlMetadataResponseSchema = z.object({
    isPreviewUrl: z.boolean(),
    org: z.string(),
    url: z.string(),
    gitUrl: z.string().nullish(),
    enableAlgoliaOnPreview: z.boolean().nullish()
});

export const GetDocsForUrlInputSchema = z.object({
    url: z.string(),
    excludeApis: z.boolean().nullish()
});

export const GetPrivateDocsForUrlInputSchema = z.object({
    url: z.string()
});

export const ListAllDocsUrlsInputSchema = z.object({
    limit: z.number().nullish(),
    page: z.number().nullish(),
    custom: z.boolean().nullish(),
    preview: z.boolean().nullish()
});

export const GetDocsConfigByIdInputSchema = z.object({
    docsConfigId: z.string()
});

// Backward-compatible types from generated code, used by downstream packages
export interface BaseUrl {
    domain: string;
    basePath: string | undefined;
}

export const DocsDefinitionField = {
    BaseUrl: "BASE_URL",
    FilesV2: "FILES_V2",
    ApisV2: "APIS_V2",
    Apis: "APIS",
    Pages: "PAGES",
    JsFiles: "JS_FILES",
    Config: "CONFIG",
    Root: "ROOT"
} as const;
export type DocsDefinitionField = (typeof DocsDefinitionField)[keyof typeof DocsDefinitionField];

export interface DocsDomainItem extends BaseUrl {
    organizationId: string;
    updatedAt: string;
}

export interface DocsUrlMetadata {
    url: string;
    org: string;
    isPreviewUrl: boolean;
    gitUrl: string | undefined;
    enableAlgoliaOnPreview: boolean;
}

export interface EnsureDocsInS3Response {
    s3Url: string;
    orgId: string;
}

export interface GetDocsFieldsRequest {
    domain: string;
    basepath?: string;
    fields: DocsDefinitionField[];
}

export interface GetDocsFieldsResponse {
    domain: string;
    baseUrl: BaseUrl | undefined;
    filesV2: Record<string, unknown> | undefined;
    apisV2: Record<string, unknown> | undefined;
    apis: Record<string, unknown> | undefined;
    pages: Record<string, unknown> | undefined;
    jsFiles: Record<string, string> | undefined;
    config: unknown | undefined;
    root: unknown | undefined;
}

export interface GetDocsConfigByIdResponse {
    docsConfig: unknown;
    apis: Record<string, unknown>;
}

export const GetDocsConfigByIdResponseSchema = z.object({
    docsConfig: DocsReadConfigSchema,
    apis: z.record(z.string(), z.unknown())
});

export const DocsDomainItemSchema = z.object({
    domain: z.string(),
    basePath: z.string().optional(),
    organizationId: z.string(),
    updatedAt: z.string()
});

export const ListAllDocsUrlsResponseSchema = z.object({
    urls: z.array(DocsDomainItemSchema)
});

export interface ListAllDocsUrlsResponse {
    urls: DocsDomainItem[];
}

export interface ListAllDocsUrlsRequest {
    page?: number;
    limit?: number;
    custom?: boolean;
    preview?: boolean;
}

export interface EnsureDocsInS3Request {
    url: string;
    basepath?: string;
}

// Backward-compatible error type for getDocsForUrl
// Previously generated by the Fern client, used by downstream packages
export namespace getDocsForUrl {
    export type Error = Error.DomainNotRegisteredError | Error.UnauthorizedError | Error._Unknown;

    export namespace Error {
        export interface DomainNotRegisteredError {
            error: "DomainNotRegisteredError";
            content?: unknown;
        }
        export interface UnauthorizedError {
            error: "UnauthorizedError";
            content: string;
        }
        export interface _Unknown {
            error: void;
            content: unknown;
        }

        export function _unknown(fetcherError: unknown): _Unknown {
            return { error: undefined as void, content: fetcherError };
        }
    }
}

export const docsV2ReadContract = {
    getDocsUrlMetadata: oc
        .route({ method: "POST", path: "/metadata-for-url" })
        .input(GetDocsUrlMetadataInputSchema)
        .output(GetDocsUrlMetadataResponseSchema),

    getDocsForUrl: oc
        .route({ method: "POST", path: "/load-with-url" })
        .input(GetDocsForUrlInputSchema)
        .output(LoadDocsForUrlResponseSchema),

    getPrivateDocsForUrl: oc
        .route({ method: "POST", path: "/private/load-with-url" })
        .input(GetPrivateDocsForUrlInputSchema)
        .output(LoadDocsForUrlResponseSchema),

    listAllDocsUrls: oc
        .route({ method: "GET", path: "/urls/list" })
        .input(ListAllDocsUrlsInputSchema)
        .output(ListAllDocsUrlsResponseSchema),

    getDocsConfigById: oc
        .route({ method: "GET", path: "/{docsConfigId}" })
        .input(GetDocsConfigByIdInputSchema)
        .output(GetDocsConfigByIdResponseSchema),

    prepopulateFdrReadS3Bucket: oc.route({ method: "POST", path: "/prepopulate-s3-bucket" }).output(z.void()),

    ensureDocsInS3: oc.route({ method: "POST", path: "/ensure-docs-in-s3" }).output(z.void()),

    getDocsFields: oc.route({ method: "POST", path: "/load-fields" }).output(z.void())
};
